import sortBy from 'lodash/sortBy';
import { createSelector } from 'reselect';

import { RootState } from 'src/config/rootStore';
import { CoachNode } from 'src/generated/webapp_gql';
import { ProviderType } from 'src/modules/user/models/user';
import { FetchStatus } from 'src/shared/models/shared';

const sortByMatchScore = (providers: CoachNode[]) => sortBy(providers, ['matchScore']).reverse();

export const getMyProviderAssignments = createSelector(
    (state: RootState) => state.providers.myProviders,
    providers => providers ?? [],
);

export const getMyProviders = createSelector(
    getMyProviderAssignments,
    (myCoachAssignments): CoachNode[] => {
        const providers = myCoachAssignments
            .filter(({ active }) => active === true)
            .map(({ coach }) => coach as CoachNode)
            .filter(provider => provider.isInAppProvider === true);

        return sortByMatchScore(providers);
    },
);

export const getMyProvidersForScheduleSessionFlow = createSelector(
    getMyProviderAssignments,
    (myCoachAssignments): CoachNode[] => {
        const providers = myCoachAssignments
            .filter(({ active }) => active === true)
            .filter(({ isPathwayAssignment }) => !isPathwayAssignment)
            .filter(({ displayOnSchedule }) => displayOnSchedule === true)
            .map(({ coach }) => coach as CoachNode)
            .filter(provider => provider.isInAppProvider === true);

        return sortByMatchScore(providers);
    },
);

export const getConversationByProviderUuid = (providerUuid: string) =>
    createSelector(
        getMyProviderAssignments,
        myCoachAssignments =>
            myCoachAssignments.find(({ coach }) => coach?.uuid === providerUuid)?.conversation,
    );

export const getMyCoaches = createSelector(getMyProviders, providers =>
    providers.filter(provider => provider.providerType === ProviderType.COACH),
);

export const getMyTherapists = createSelector(getMyProviders, providers =>
    providers.filter(provider => provider.providerType === ProviderType.THERAPIST),
);

export const getProviderByUuid = (providerUuid: string) =>
    createSelector(getMyProviders, providers =>
        providers.find(provider => provider.uuid === providerUuid),
    );

export const getMyProvidersUuid = createSelector(getMyProviders, providers =>
    providers.map(provider => provider.uuid),
);

export const isUserProvidersFetchCompleted = (state: RootState) =>
    state.providers.fetchStatus === FetchStatus.Completed;

export const isProviderAssignmentStillActive = createSelector(
    getMyProviderAssignments,
    (_: RootState, providerUuid: string) => providerUuid,
    (assignments, providerUuid) => {
        const assignment = assignments.find(({ coach }) => coach?.uuid === providerUuid);

        return assignment?.active ?? false;
    },
);
